import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiCamera, HiDotsVertical, HiTrash, HiUser, HiUserAdd } from "react-icons/hi";
import AvatarCropModal from "../../pages/profile/AvatarCropModal";
import Tippy from "@tippyjs/react";
import ChatAvatar from "./ChatAvatar";
import ChatModal, { modalButton } from "./ChatModal";
import MemberPicker from "./MemberPicker";
import { useChat } from "./ChatProvider";
import { formatLastSeen, otherMemberId, userName } from "./chatUtils";

const inputClass =
  "w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-70";

function MemberMenu({ onMakeAdmin, onRemoveAdmin, onRemove }) {
  const [open, setOpen] = useState(false);
  const item = "block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700";
  const run = (fn) => () => {
    setOpen(false);
    fn();
  };
  return (
    <Tippy
      theme="irg-plain"
      arrow={false}
      visible={open}
      onClickOutside={() => setOpen(false)}
      interactive
      placement="bottom-end"
      appendTo={() => document.body}
      zIndex={90}
      content={
        <div className="min-w-[170px] py-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
          {onMakeAdmin && <button type="button" className={item} onClick={run(onMakeAdmin)}>Make group admin</button>}
          {onRemoveAdmin && <button type="button" className={item} onClick={run(onRemoveAdmin)}>Remove as admin</button>}
          <button type="button" className={`${item} text-red-600`} onClick={run(onRemove)}>Remove from group</button>
        </div>
      }
    >
      <button type="button" onClick={() => setOpen((v) => !v)} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Member options">
        <HiDotsVertical className="w-4 h-4" />
      </button>
    </Tippy>
  );
}

function ConversationInfoModal({ conversationId, onClose }) {
  const {
    conversations, users, me, updateGroup, addMembers, removeMember, setMemberRole, setMuted, setConversationPinned,
    hideConversation, deleteGroup, setGroupPhoto, removeGroupPhoto,
  } = useChat();
  const conversation = conversations[conversationId];
  const [name, setName] = useState(conversation?.name || "");
  const [description, setDescription] = useState(conversation?.description || "");
  const [adding, setAdding] = useState(false);
  const [toAdd, setToAdd] = useState([]);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // "me" | "everyone"
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUpload, setPhotoUpload] = useState({ saving: false, progress: 0, error: "" });
  const photoInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (!conversation) return null;

  const openProfile = (userId) => {
    onClose();
    navigate(userId === me?.id ? "/profile" : `/profile/${userId}`);
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const deleteDialog = confirmDelete && (
    <ChatModal
      title={confirmDelete === "everyone" ? "Delete group for everyone?" : "Delete this chat?"}
      onClose={() => setConfirmDelete(null)}
      footer={
        <>
          <button type="button" className={modalButton.secondary} onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button
            type="button"
            className={modalButton.danger}
            disabled={busy}
            onClick={async () => {
              const action = confirmDelete === "everyone" ? deleteGroup : hideConversation;
              if (await run(() => action(conversationId))) onClose();
            }}
          >
            {confirmDelete === "everyone" ? "Delete for everyone" : "Delete chat"}
          </button>
        </>
      }
    >
      {confirmDelete === "everyone" ? (
        <p className="text-sm">
          <strong>{conversation.name}</strong>, all of its messages and files will be permanently deleted for all{" "}
          {conversation.members.length} members. This can&apos;t be undone.
        </p>
      ) : (
        <p className="text-sm">
          The chat and its history will be removed from your list. Other people keep their copy. If someone sends a new
          message, the chat comes back with only the new messages.
        </p>
      )}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </ChatModal>
  );

  const deleteChatButton = (
    <button type="button" onClick={() => setConfirmDelete("me")} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-tour="chat-delete">
      <HiTrash className="w-4 h-4" /> Delete chat
    </button>
  );

  const pinToggle = (
    <div className="flex items-center justify-between gap-3 py-2">
      <span>
        <label htmlFor={`chat-pin-${conversationId}`} className="block text-sm font-medium cursor-pointer">
          Pin to top
        </label>
        <span className="block text-xs text-gray-500">Keep this chat at the top of your list (only for you)</span>
      </span>
      <input id={`chat-pin-${conversationId}`} type="checkbox" className="rounded text-blue-600" checked={!!conversation.pinnedAt} onChange={(e) => run(() => setConversationPinned(conversationId, e.target.checked))} />
    </div>
  );

  const muteToggle = (
    <div className="flex items-center justify-between gap-3 py-2">
      <span>
        <label htmlFor={`chat-mute-${conversationId}`} className="block text-sm font-medium cursor-pointer">
          Mute notifications
        </label>
        <span className="block text-xs text-gray-500">No sound, desktop alert or unread badge for this chat</span>
      </span>
      <input id={`chat-mute-${conversationId}`} type="checkbox" className="rounded text-blue-600" checked={!!conversation.muted} onChange={(e) => run(() => setMuted(conversationId, e.target.checked))} />
    </div>
  );

  if (conversation.type === "direct") {
    const other = users[otherMemberId(conversation, me?.id)];
    return (
      <ChatModal title="Chat details" onClose={onClose}>
        <div className="flex flex-col items-center text-center gap-2 pb-4 border-b border-gray-200 dark:border-gray-700">
          <ChatAvatar name={userName(other)} src={other?.avatarUrl || other?.avatarThumbUrl} seed={other?.id} size="lg" online={!!other?.online} />
          <p className="text-base font-semibold">{userName(other)}</p>
          {(other?.jobTitle || other?.department) && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{[other.jobTitle, other.department].filter(Boolean).join(" · ")}</p>
          )}
          <p className="text-sm text-gray-500">{other?.email}</p>
          <p className="text-xs text-gray-500">{formatLastSeen(other)}</p>
          {other && (
            <button type="button" onClick={() => openProfile(other.id)} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
              <HiUser className="w-4 h-4" /> View full profile
            </button>
          )}
        </div>
        <div className="pt-2">
          {pinToggle}
          {muteToggle}
        </div>
        <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700">{deleteChatButton}</div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {deleteDialog}
      </ChatModal>
    );
  }

  const myRole = conversation.members.find((m) => m.userId === me?.id)?.role;
  const isAdmin = myRole === "admin";
  const members = [...conversation.members].sort(
    (a, b) => (a.role === "admin" ? -1 : 0) - (b.role === "admin" ? -1 : 0) || userName(users[a.userId]).localeCompare(userName(users[b.userId]))
  );
  const detailsChanged = name.trim() !== (conversation.name || "") || description.trim() !== (conversation.description || "");

  if (adding) {
    return (
      <ChatModal
        title="Add people"
        onClose={() => setAdding(false)}
        footer={
          <>
            <button type="button" className={modalButton.secondary} onClick={() => setAdding(false)}>Cancel</button>
            <button
              type="button"
              className={modalButton.primary}
              disabled={!toAdd.length || busy}
              onClick={async () => {
                if (await run(() => addMembers(conversationId, toAdd))) {
                  setToAdd([]);
                  setAdding(false);
                }
              }}
            >
              Add {toAdd.length || ""}
            </button>
          </>
        }
      >
        <MemberPicker selected={toAdd} onChange={setToAdd} exclude={conversation.members.map((m) => m.userId)} />
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </ChatModal>
    );
  }

  if (confirmLeave) {
    return (
      <ChatModal
        title="Leave group?"
        onClose={() => setConfirmLeave(false)}
        footer={
          <>
            <button type="button" className={modalButton.secondary} onClick={() => setConfirmLeave(false)}>Cancel</button>
            <button
              type="button"
              className={modalButton.danger}
              disabled={busy}
              onClick={async () => {
                if (await run(() => removeMember(conversationId, me.id))) onClose();
              }}
            >
              Leave
            </button>
          </>
        }
      >
        <p className="text-sm">You&apos;ll stop receiving messages from <strong>{conversation.name}</strong> and lose access to its history.</p>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </ChatModal>
    );
  }

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return setError("Choose a PNG, JPEG, WebP or GIF image.");
    setPhotoUpload({ saving: false, progress: 0, error: "" });
    setPhotoFile(file);
  };

  const savePhoto = async (blob) => {
    setPhotoUpload({ saving: true, progress: 0, error: "" });
    try {
      await setGroupPhoto(conversationId, blob, (progress) => setPhotoUpload((u) => ({ ...u, progress })));
      setPhotoFile(null);
      setPhotoUpload({ saving: false, progress: 0, error: "" });
    } catch (err) {
      setPhotoUpload({ saving: false, progress: 0, error: err.response?.data?.message || "Upload failed. Try again." });
    }
  };

  return (
    <ChatModal title="Group details" onClose={onClose} width="max-w-lg">
      <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4" data-tour="chat-group-photo">
          <div className="relative">
            <ChatAvatar name={conversation.name} src={conversation.avatarUrl || conversation.avatarThumbUrl} seed={conversation.id} size="lg" group />
            {isAdmin && (
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#2470B7] text-white flex items-center justify-center border-2 border-white dark:border-gray-900 hover:bg-[#1d5a93]"
                title="Change group photo"
                aria-label="Change group photo"
              >
                <HiCamera className="w-4 h-4" />
              </button>
            )}
            <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickPhoto} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">{conversation.name}</p>
            <p className="text-xs text-gray-500">{conversation.members.length} members</p>
            {isAdmin && conversation.avatarUrl && (
              <button type="button" onClick={() => run(() => removeGroupPhoto(conversationId))} className="text-xs text-red-600 hover:underline mt-1">
                Remove photo
              </button>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="chat-info-name" className="block text-sm font-medium mb-1">Group name</label>
          <input id="chat-info-name" className={inputClass} value={name} maxLength={120} disabled={!isAdmin} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="chat-info-description" className="block text-sm font-medium mb-1">Description</label>
          <textarea id="chat-info-description" rows={2} className={inputClass} value={description} disabled={!isAdmin} onChange={(e) => setDescription(e.target.value)} placeholder={isAdmin ? "What is this group for?" : ""} />
        </div>
        {isAdmin && detailsChanged && (
          <div className="flex justify-end">
            <button type="button" className={modalButton.primary} disabled={busy || !name.trim()} onClick={() => run(() => updateGroup(conversationId, { name: name.trim(), description: description.trim() }))}>
              Save changes
            </button>
          </div>
        )}
        {pinToggle}
        {muteToggle}
      </div>

      <div className="pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">{members.length} members</p>
          {isAdmin && (
            <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-1 text-sm text-[#2470B7] hover:underline">
              <HiUserAdd className="w-4 h-4" /> Add people
            </button>
          )}
        </div>
        <ul className="space-y-1">
          {members.map((member) => {
            const user = users[member.userId];
            const isMe = member.userId === me?.id;
            return (
              <li key={member.userId} className="flex items-center gap-3 py-1.5">
                <ChatAvatar name={userName(user)} src={user?.avatarThumbUrl} seed={member.userId} size="sm" online={!!user?.online} />
                <span className="flex-1 min-w-0">
                  <button type="button" onClick={() => openProfile(member.userId)} className="block max-w-full text-left text-sm font-medium truncate hover:underline">
                    {userName(user)}{isMe ? " (you)" : ""}
                  </button>
                  <span className="block text-xs text-gray-500 truncate">{formatLastSeen(user)}</span>
                </span>
                {member.role === "admin" && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Admin</span>
                )}
                {isAdmin && !isMe && (
                  <MemberMenu
                    onMakeAdmin={member.role !== "admin" ? () => run(() => setMemberRole(conversationId, member.userId, "admin")) : null}
                    onRemoveAdmin={member.role === "admin" ? () => run(() => setMemberRole(conversationId, member.userId, "member")) : null}
                    onRemove={() => run(() => removeMember(conversationId, member.userId))}
                  />
                )}
              </li>
            );
          })}
        </ul>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-x-5 gap-y-2">
          <button type="button" onClick={() => setConfirmLeave(true)} className="text-sm text-red-600 hover:underline">
            Leave group
          </button>
          {deleteChatButton}
          {isAdmin && (
            <button type="button" onClick={() => setConfirmDelete("everyone")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:underline">
              <HiTrash className="w-4 h-4" /> Delete group for everyone
            </button>
          )}
        </div>
      </div>
      {deleteDialog}
      {photoFile && (
        <AvatarCropModal
          file={photoFile}
          onCancel={() => setPhotoFile(null)}
          onSave={savePhoto}
          saving={photoUpload.saving}
          progress={photoUpload.progress}
          error={photoUpload.error}
          title="Crop group photo"
          hint="Drag to reposition. Everyone in the group sees this photo."
        />
      )}
    </ChatModal>
  );
}

export default ConversationInfoModal;
